import { useState, useEffect, useRef } from 'react'

/**
 * SSR-first veri yükleme deseni — tek kaynak (DRY), tek sorumluluk (SOLID).
 *
 * Davranış:
 *  - `initial` (sunucudan SSR ile gelen veri) varsa: onunla başlar ve İLK client
 *    fetch'ini ATLAR → içerik ham HTML'de kalır (SEO/AEO/GEO: botlar görür).
 *  - `initial` yoksa: mount'ta bir kez fetch eder.
 *  - Her iki durumda da `intervalMs`'de bir arka planda sessizce yeniler.
 *
 * Not: "initial yok" demek için çağıran tarafta boş koleksiyonları `undefined`'a
 * çevirin (ör. boş `{}`/`[]` → `undefined`); hook genel olduğu için boşluk
 * semantiğini bilmez.
 *
 * @param fetcher    Veriyi çeken async fonksiyon. Her render'da yeni referans olabilir;
 *                   hook her zaman en güncelini çağırır (interval effect'i yeniden kurulmaz).
 * @param initial    Sunucudan gelen başlangıç verisi (null/undefined → client fetch).
 * @param intervalMs Periyodik yenileme aralığı (ms).
 */
export function usePolledData<T>(
  fetcher: () => Promise<T>,
  initial: T | null | undefined,
  intervalMs: number,
): { data: T | undefined; loading: boolean } {
  const hasInitial = initial != null
  const [data, setData] = useState<T | undefined>(initial ?? undefined)
  const [loading, setLoading] = useState(!hasInitial)

  // Fetcher referansını ref'te tut: lang gibi değişkenlere bağlı olsa bile
  // interval effect'ini yeniden kurmadan en güncel fetcher çağrılır.
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  useEffect(() => {
    let active = true
    const run = async () => {
      try {
        const result = await fetcherRef.current()
        if (active) {
          setData(result)
          setLoading(false)
        }
      } catch {
        // Yenileme hatası sessiz: son başarılı veriyi koru, sadece loading'i kapat.
        if (active) setLoading(false)
      }
    }
    if (!hasInitial) run()
    const id = setInterval(run, intervalMs)
    return () => {
      active = false
      clearInterval(id)
    }
  }, [hasInitial, intervalMs])

  return { data, loading }
}
